import * as React from 'react';
import * as moment from 'moment-timezone';
import Avatar from './Avatar';
import ProgressIndicator from './ProgressIndicator';
import axios from '../lib/axios';

interface User {
  slug: string;
  profile_url: string;
  name: string;

  id: string;
  avatar_url?: string;
  email?: string;
  phone_number?: string;
  roles?: string[];
  companies?: string[];
  campuses?: string[];
  groups?: string[];
  source?: string;
  staff_url?: string;
  created_at?: string;
  updated_at?: string;
  timezone: string;
}

interface TaskCompletion extends User {
  date: string;
}

interface TaskProps {
  name: string;
  notes?: string;
  due_date?: string;
  completedBy?: TaskCompletion;
  currentUser: User;
  completed: boolean;
  readonly: boolean;
  completeAPI: string;
  uncompleteAPI: string;
  total_companies: number;
  total_companies_completed: number;
}

export interface TaskState {
  completed: boolean;
  completedBy: TaskCompletion;
}

export default class Task extends React.Component<TaskProps, TaskState> {
  public static defaultProps: TaskProps = {
    name: '',
    completed: false,
    readonly: false,
    completeAPI: '',
    uncompleteAPI: '',
    total_companies: 0,
    total_companies_completed: 0,
    currentUser: {
      id: '',
      slug: '',
      profile_url: '',
      name: '',
      timezone: moment.tz.guess(),
    },
  };

  constructor(props: TaskProps) {
    super(props);

    this.state = {
      completedBy: props.completedBy,
      completed: props.completed,
    };
  }

  private storeChanges() {
    axios.patch(this.state.completed ? this.props.completeAPI : this.props.uncompleteAPI);
  }

  private onClick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newState: TaskState = {
      completed: !this.state.completed,
      completedBy: !this.state.completed
        ? { date: moment().format(), ...this.props.currentUser }
        : null,
    };
    this.setState(newState, this.storeChanges);
  };

  private renderCheck() {
    return (
      <div className="u-pr-3">
        <input type="checkbox" checked={this.state.completed} onChange={this.onClick} />
      </div>
    );
  }

  private renderCompleter() {
    const completer: JSX.Element =
      this.state.completedBy.id == this.props.currentUser.id ? (
        <strong>you</strong>
      ) : (
        <a href={this.state.completedBy.profile_url}>{this.state.completedBy.name}</a>
      );

    const date: string = moment(this.state.completedBy.date)
      .tz(this.props.currentUser.timezone)
      .format('MMMM D, YYYY');
    return (
      <div className="u-flex u-items-center">
        <Avatar
          className="u-w-5 u-h-5 u-text-xs"
          href={this.state.completedBy.profile_url}
          name={this.state.completedBy.name}
          src={this.state.completedBy.avatar_url}
          type="user"
        />
        <div className="u-text-xs u-ml-2 u-text-grey-dark">
          Completed by {completer} {date}
        </div>
      </div>
    );
  }

  private renderDue() {
    const classNames: string[] = [
      'u-rounded u-whitespace-no-wrap u-flex-grow-0 u-text-white u-px-2 u-py-1 u-text-sm u-flex u-items-center u-leading-none',
    ];
    const momentDueDate = moment(this.props.due_date);
    if (this.state.completed) {
      classNames.push('u-bg-green');
    } else if (momentDueDate.isBefore(moment())) {
      classNames.push('u-bg-red');
    } else if (momentDueDate.isBefore(moment().add(7, 'days'))) {
      classNames.push('u-bg-yellow');
    } else {
      classNames.push('u-bg-grey');
    }
    return (
      <div className={classNames.join(' ')}>
        <i className="Icon Icon-clock u-text-base" />
        {momentDueDate.tz(this.props.currentUser.timezone).format('MMMM D')}
      </div>
    );
  }

  public render() {
    const classNames: string[] = ['u-rounded u-border u-p-4 u-border-l-6 u-mb-2'];
    if (this.state.completed) {
      classNames.push('u-border-green');
    } else {
      classNames.push('u-border-grey-light');
    }
    return (
      <div className={classNames.join(' ')}>
        <div className="u-flex u-items-start">
          {!this.props.readonly && this.renderCheck()}
          <div className="u-flex-grow u-flex u-flex-col">
            <div className="u-flex u-justify-between">
              <div
                className={`${
                  this.state.completed ? 'u-line-through u-text-grey' : 'u-text-grey-darker'
                } u-font-semibold`}>
                {this.props.name}
              </div>
              {this.props.due_date && this.renderDue()}
            </div>
            <div className="Readability">
              <div
                className={this.state.completed ? 'u-line-through u-text-grey' : 'u-text-grey-darker'}
                dangerouslySetInnerHTML={{ __html: this.props.notes }}
              />
            </div>
            <div className="u-mt-2">
              {this.state.completed && this.renderCompleter()}
            </div>
          </div>
        </div>
        {this.props.readonly && (
          <div className="u-mt-4">
            <ProgressIndicator steps={this.props.total_companies} currentStep={this.props.total_companies_completed} progressBar={true} extraCopy="companies" />
          </div>
        )}
      </div>
    );
  }
}
